/**
 * Shared asset sub-route configs.
 *
 * These are relative-path route entries that are mounted under both:
 *   - `/assets/:assetId/...`          (from assets/routes.tsx)
 *   - `/activities/:issueId/asset/:assetId/...`  (from activity/routes.tsx)
 *
 * Each entry carries `handle.panel` so PanelShell's `transitionKey` updates
 * whenever the user navigates between sub-routes, driving the slide-in animation.
 *
 * CLAUDE.md rule: assets/routes.tsx and activity/routes.tsx changes are
 * additive — existing flat entries are preserved for legacy URL compatibility.
 */
import { PermissionValues as Permissions } from '@gripp/shared-logic';
import { ActivityEdit } from '../activity/components/activity-edit';
import { ActivityReassign } from '../activity/components/activity-reassign';
import { ActivityRevise } from '../activity/components/activity-revise';
import { AssetAlertFormPage } from './components/asset-alert-form-page';
import { AssetRoutineAlerts } from './components/asset-routine-alerts';
import { AssetRoutineSubmit } from './components/asset-routine-submit';
import { AssetRoutines } from './components/asset-routines';
import { AssetRoutinesAll } from './components/asset-routines-all';
import { AssetRoutinesRearrange } from './components/asset-routines-rearrange';
import { AssetActivity } from './components/assetActivity';
import { AssetChatPanel } from './components/assetChatPanel';
import { AssetDetail } from './components/assetDetail';
import { AssetDetailFilter } from './components/assetDetailFilter';
import { AssetFileCreate } from './components/assetFileCreate';
import { AssetFileEdit } from './components/assetFileEdit';
import { AssetFilePreview } from './components/assetFilePreview';
import { AssetFiles } from './components/assetFiles';
import { AssetImageView } from './components/assetImageView';
import { AssetNewIssue } from './components/assetNewIssue';
import { AssetSubRouteGuestGate } from './components/assetSubRouteGuestGate';
import { EditAsset } from './components/edit';

/**
 * Relative-path route configs for all sub-routes under an asset.
 * Mount under `<parentPath>/:assetId/` in the route tree.
 *
 * `requiredPermission` alone does not keep a Guest off these routes (see
 * `AssetSubRouteGuestGate`'s doc comment), so every route below wraps its
 * element in that gate EXCEPT the three that already compute their own,
 * narrower guest determination and partially render for a Guest rather than
 * refusing outright: `activity/all` and `activity/:issueId` (the allowlisted
 * header + own-conversation composition roots) and `files/all` (redirects
 * itself). Adding a new sub-route means adding the gate too, not opting in.
 */
export const assetSubRoutes = [
  {
    path: 'edit',
    element: (
      <AssetSubRouteGuestGate>
        <EditAsset />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-edit', title: 'topNav.editAsset' },
      header: { titleKey: 'topNav.editAsset', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'image',
    element: (
      <AssetSubRouteGuestGate>
        <AssetImageView />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-image', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  // Asset Chat. `chat/thread/:issueId/settings` and `chat/sidechat/:messageId`
  // are placeholders: the settings frame arrives with Participants and Side
  // Chat has no entry point until the row overflow menu. All four share one
  // element, AssetChatPanel, which reads which sub-path it is from the URL.
  // Mind the `:issueId` collision documented on the existing `activity/
  // :issueId` entry below: under the activities prefix this spread binds
  // `:issueId` for the surrounding activity too, so on the bare `chat` route
  // — which names no thread — the param still resolves, to the wrong thing.
  // AssetChatPanel therefore matches `/chat/thread/:id` out of the pathname
  // rather than trusting useParams(), the same way assetActivity.tsx
  // re-derives the OUTER context id.
  {
    path: 'chat',
    element: (
      <AssetSubRouteGuestGate>
        <AssetChatPanel />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-chat', title: 'chat.panel.title' },
      header: { titleKey: 'chat.panel.title', icon: 'qrcode' },
      requiredPermission: Permissions.issue.read,
    },
  },
  {
    path: 'chat/thread/:issueId',
    element: (
      <AssetSubRouteGuestGate>
        <AssetChatPanel />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-chat-thread', title: 'activities.detail.title' },
      header: { titleKey: 'chat.panel.title', icon: 'qrcode' },
      requiredPermission: Permissions.issue.read,
    },
  },
  {
    path: 'chat/thread/:issueId/settings',
    element: (
      <AssetSubRouteGuestGate>
        <AssetChatPanel />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-chat-thread-settings', title: 'activities.detail.title' },
      header: { titleKey: 'chat.panel.title', icon: 'qrcode' },
      requiredPermission: Permissions.issue.read,
    },
  },
  {
    path: 'chat/sidechat/:messageId',
    element: (
      <AssetSubRouteGuestGate>
        <AssetChatPanel />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-chat-sidechat', title: 'activity.sideChat.title' },
      header: { titleKey: 'chat.panel.title', icon: 'qrcode' },
      requiredPermission: Permissions.issue.read,
    },
  },
  {
    path: 'activity/new',
    element: (
      <AssetSubRouteGuestGate>
        <AssetNewIssue />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-activity-new', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.issue.create,
    },
  },
  {
    path: 'activity/all',
    element: <AssetDetail />,
    handle: {
      panel: { key: 'asset-activity-all', title: 'topNav.assetDetail' },
      header: { titleKey: 'topNav.assetDetail', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'activity/filters',
    element: (
      <AssetSubRouteGuestGate>
        <AssetDetailFilter />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-activity-filters', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    // A Guest's one allowlisted sub-route: their own conversation. Deliberately
    // NOT wrapped in AssetSubRouteGuestGate -- the server scopes which
    // issue/activity a Guest may read to their own, so refusing here would
    // take away the one thing a Guest's asset page exists to show.
    path: 'activity/:issueId',
    element: <AssetActivity />,
    handle: {
      panel: { key: 'asset-activity', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'activity/:issueId/edit',
    element: (
      <AssetSubRouteGuestGate>
        <ActivityEdit />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-activity-edit', title: 'activity.menu.title' },
      header: { titleKey: 'activity.menu.title', icon: 'bell-outline' },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: 'activity/:issueId/edit/reassign',
    element: (
      <AssetSubRouteGuestGate>
        <ActivityReassign />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-activity-edit-reassign', title: 'activity.menu.title' },
      header: { titleKey: 'activity.menu.title', icon: 'bell-outline' },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: 'activity/:issueId/edit/revise',
    element: (
      <AssetSubRouteGuestGate>
        <ActivityRevise />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-activity-edit-revise', title: 'activity.menu.title' },
      header: { titleKey: 'activity.menu.title', icon: 'bell-outline' },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    // Lists every file on the asset, not the folded preview a guest's
    // allowlisted composition roots show -- AssetFiles computes its own
    // (corrected) guest redirect rather than using this shared gate.
    path: 'files/all',
    element: <AssetFiles />,
    handle: {
      panel: { key: 'asset-files', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'files/new',
    element: (
      <AssetSubRouteGuestGate>
        <AssetFileCreate />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-file-create', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.update,
    },
  },
  {
    path: 'files/:mediaId',
    element: (
      <AssetSubRouteGuestGate>
        <AssetFilePreview />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-file-preview', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'files/:mediaId/edit',
    element: (
      <AssetSubRouteGuestGate>
        <AssetFileEdit />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-file-edit', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.update,
    },
  },
  {
    path: 'routines',
    element: (
      <AssetSubRouteGuestGate>
        <AssetRoutines />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-routines', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/all',
    element: (
      <AssetSubRouteGuestGate>
        <AssetRoutinesAll />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-routines-all', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/edit',
    element: (
      <AssetSubRouteGuestGate>
        <AssetRoutinesRearrange />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-routines-edit', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.update,
    },
  },
  {
    path: 'routines/:routineId',
    element: (
      <AssetSubRouteGuestGate>
        <AssetRoutineSubmit />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-routine-submit', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/all/:routineId',
    element: (
      <AssetSubRouteGuestGate>
        <AssetRoutineSubmit />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-routine-submit-all', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/:routineId/alerts',
    element: (
      <AssetSubRouteGuestGate>
        <AssetRoutineAlerts />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-routine-alerts', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/:routineId/alerts/new',
    element: (
      <AssetSubRouteGuestGate>
        <AssetAlertFormPage />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-routine-alert-new', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'routines/:routineId/alerts/:alertId/edit',
    element: (
      <AssetSubRouteGuestGate>
        <AssetAlertFormPage />
      </AssetSubRouteGuestGate>
    ),
    handle: {
      panel: { key: 'asset-routine-alert-edit', title: 'assets.menu.title' },
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
];
